import type { HTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { AGEING_BUCKETS, ageingTone, type AgeingBucketKey, type AgeingTone } from '@bright/shared';
import { formatNumber } from '@/lib/format';

/**
 * How long the current holder has had it.
 *
 * Always a soft background with the strong colour as text, and always with the
 * day count spelled out — the ramp is a fast read, the number is the fact.
 * Colour alone would fail anyone who cannot separate the orange step from the
 * red one, and the ramp's first two steps carry no colour at all because new
 * stock is not news.
 */

const TONE_CLASS: Record<AgeingTone, string> = {
  fresh: 'bg-age-fresh-soft text-age-fresh',
  settled: 'bg-age-settled-soft text-age-settled',
  watch: 'bg-age-watch-soft text-age-watch',
  warn: 'bg-age-warn-soft text-age-warn',
  stale: 'bg-age-stale-soft text-age-stale',
  critical: 'bg-age-critical-soft text-age-critical',
  severe: 'bg-age-severe-soft text-age-severe',
};

const TONE_BY_BUCKET: Record<AgeingBucketKey, AgeingTone> = AGEING_BUCKETS.reduce(
  (acc, bucket) => {
    acc[bucket.key] = bucket.tone;
    return acc;
  },
  {} as Record<AgeingBucketKey, AgeingTone>,
);

/**
 * The ramp's classes, defined once.
 *
 * Every screen that draws an ageing bar or figure needs these, and each used to
 * keep its own copy — so adding the two new bands broke four files in exactly
 * the same way. One map, imported.
 */
export const AGEING_BAR_CLASS: Record<AgeingTone, string> = {
  fresh: 'bg-age-fresh',
  settled: 'bg-age-settled',
  watch: 'bg-age-watch',
  warn: 'bg-age-warn',
  stale: 'bg-age-stale',
  critical: 'bg-age-critical',
  severe: 'bg-age-severe',
};

export const AGEING_TEXT_CLASS: Record<AgeingTone, string> = {
  fresh: 'text-age-fresh',
  settled: 'text-age-settled',
  watch: 'text-age-watch',
  warn: 'text-age-warn',
  stale: 'text-age-stale',
  critical: 'text-age-critical',
  severe: 'text-age-severe',
};

export function ageingToneClass(tone: AgeingTone): string {
  return TONE_CLASS[tone];
}

export function ageingToneForBucket(bucket: AgeingBucketKey): AgeingTone {
  return TONE_BY_BUCKET[bucket];
}

export interface AgeingChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** Days the current holder has held the vehicle. `null` means no date to count from yet. */
  days: number | null | undefined;
  size?: 'sm' | 'md';
  /** Shows the bucket range next to the count, e.g. `62 d · 61–90`. */
  showBucket?: boolean;
}

export function AgeingChip({
  days,
  size = 'md',
  showBucket = false,
  className,
  ...rest
}: AgeingChipProps) {
  const tone = ageingTone(days);

  // Absent is the common case on this data, and 900 bordered chips saying "–"
  // shout louder than the numbers that matter. Unknown is a quiet dash.
  if (tone === null || days === null || days === undefined) {
    return (
      <span
        className={clsx(
          'inline-flex items-center font-data text-faint',
          size === 'sm' ? 'h-5 text-label' : 'h-6 text-micro',
          className,
        )}
        title="No billing date, so the holding period is unknown"
        {...rest}
      >
        —
      </span>
    );
  }

  const bucket = AGEING_BUCKETS.find((b) => days >= b.min && (b.max === null || days <= b.max));
  const whole = Math.max(0, Math.round(days));

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-sm font-data font-medium tabular-nums whitespace-nowrap',
        size === 'sm' ? 'h-5 px-1.5 text-label' : 'h-6 px-2 text-micro',
        TONE_CLASS[tone],
        className,
      )}
      title={`${formatNumber(whole)} days in stock — ${bucket?.label ?? ''}`.trim()}
      {...rest}
    >
      <span>{formatNumber(whole)}</span>
      <span className="opacity-70">d</span>
      {showBucket && bucket ? (
        <span className="border-l border-current/25 pl-1 opacity-70">{bucket.short}</span>
      ) : null}
    </span>
  );
}
